/**
 * Synthèse des risques
 * Affiche le statut CMR, les pictogrammes GHS, et les codes H
 */
import { SubstanceFiche } from '../../services/api'
import { GHSPictogramList } from '../ghs/GHSPictogram'
import Card from '../ui/Card'

interface RiskSummaryProps {
  dangers: SubstanceFiche['dangers']
}

// Descriptions des codes H (phrases de risque)
const H_CODE_DESCRIPTIONS: Record<string, string> = {
  H200: 'Explosif instable',
  H201: 'Explosif; danger d\'explosion en masse',
  H202: 'Explosif; danger sérieux de projection',
  H203: 'Explosif; danger d\'incendie, d\'effet de souffle ou de projection',
  H220: 'Gaz extrêmement inflammable',
  H221: 'Gaz inflammable',
  H222: 'Aérosol extrêmement inflammable',
  H223: 'Aérosol inflammable',
  H224: 'Liquide et vapeurs extrêmement inflammables',
  H225: 'Liquide et vapeurs très inflammables',
  H226: 'Liquide et vapeurs inflammables',
  H228: 'Matière solide inflammable',
  H240: 'Peut exploser sous l\'effet de la chaleur',
  H241: 'Peut s\'enflammer ou exploser sous l\'effet de la chaleur',
  H242: 'Peut s\'enflammer sous l\'effet de la chaleur',
  H250: 'S\'enflamme spontanément au contact de l\'air',
  H251: 'Matière auto-échauffante; peut s\'enflammer',
  H252: 'Matière auto-échauffante en grandes quantités',
  H260: 'Dégage au contact de l\'eau des gaz inflammables qui peuvent s\'enflammer spontanément',
  H261: 'Dégage au contact de l\'eau des gaz inflammables',
  H270: 'Peut provoquer ou aggraver un incendie; comburant',
  H271: 'Peut provoquer un incendie ou une explosion; comburant puissant',
  H272: 'Peut aggraver un incendie; comburant',
  H280: 'Contient un gaz sous pression; peut exploser sous l\'effet de la chaleur',
  H281: 'Contient un gaz réfrigéré; peut causer des brûlures ou blessures cryogéniques',
  H290: 'Peut être corrosif pour les métaux',
  H300: 'Mortel en cas d\'ingestion',
  H301: 'Toxique en cas d\'ingestion',
  H302: 'Nocif en cas d\'ingestion',
  H304: 'Peut être mortel en cas d\'ingestion et de pénétration dans les voies respiratoires',
  H310: 'Mortel par contact cutané',
  H311: 'Toxique par contact cutané',
  H312: 'Nocif par contact cutané',
  H314: 'Provoque des brûlures de la peau et des lésions oculaires graves',
  H315: 'Provoque une irritation cutanée',
  H317: 'Peut provoquer une allergie cutanée',
  H318: 'Provoque des lésions oculaires graves',
  H319: 'Provoque une sévère irritation des yeux',
  H330: 'Mortel par inhalation',
  H331: 'Toxique par inhalation',
  H332: 'Nocif par inhalation',
  H334: 'Peut provoquer des symptômes allergiques ou d\'asthme ou des difficultés respiratoires par inhalation',
  H335: 'Peut irriter les voies respiratoires',
  H336: 'Peut provoquer somnolence ou vertiges',
  H340: 'Peut induire des anomalies génétiques',
  H341: 'Susceptible d\'induire des anomalies génétiques',
  H350: 'Peut provoquer le cancer',
  H351: 'Susceptible de provoquer le cancer',
  H360: 'Peut nuire à la fertilité ou au fœtus',
  H361: 'Susceptible de nuire à la fertilité ou au fœtus',
  H362: 'Peut être nocif pour les bébés nourris au lait maternel',
  H370: 'Risque avéré d\'effets graves pour les organes',
  H371: 'Risque présumé d\'effets graves pour les organes',
  H372: 'Risque avéré d\'effets graves pour les organes à la suite d\'expositions répétées',
  H373: 'Risque présumé d\'effets graves pour les organes à la suite d\'expositions répétées',
  H400: 'Très toxique pour les organismes aquatiques',
  H410: 'Très toxique pour les organismes aquatiques, entraîne des effets néfastes à long terme',
  H411: 'Toxique pour les organismes aquatiques, entraîne des effets néfastes à long terme',
  H412: 'Nocif pour les organismes aquatiques, entraîne des effets néfastes à long terme',
  H413: 'Peut être nocif à long terme pour les organismes aquatiques',
  H420: 'Nuit à la santé publique et à l\'environnement en détruisant l\'ozone dans la haute atmosphère',
}

export default function RiskSummary({ dangers }: RiskSummaryProps) {
  const { codes_h, pictogrammes, cmr } = dangers

  // Convertir les pictogrammes en codes pour le composant
  const pictogramCodes = pictogrammes?.map(p => p.code) || []

  return (
    <Card title="Synthèse des risques" className="border-l-4 border-l-red-500">
      {/* Alerte CMR en premier si applicable */}
      {cmr?.est_cmr && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-8 h-8 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-red-800">
                Substance CMR - {cmr.categorie || 'Catégorie non précisée'}
              </h3>
              <p className="text-red-700 text-sm mt-1">
                {cmr.detail || 'Cancérogène, Mutagène ou Reprotoxique'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pictogrammes GHS */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          Pictogrammes de danger
        </h3>
        {pictogramCodes.length > 0 ? (
          <GHSPictogramList codes={pictogramCodes} size="lg" />
        ) : (
          <p className="text-gray-500 italic">Aucun pictogramme associé</p>
        )}
      </div>

      {/* Codes H (phrases de risque) */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          Mentions de danger (codes H)
        </h3>
        {codes_h && codes_h.length > 0 ? (
          <div className="space-y-2">
            {codes_h.map((code) => {
              const description = H_CODE_DESCRIPTIONS[code] || 'Description non disponible'
              // Déterminer la couleur selon le type de code
              let colorClass = 'bg-gray-100 text-gray-800'
              if (code.match(/H[23][0-9]{2}/)) colorClass = 'bg-orange-100 text-orange-800' // Physique
              if (code.match(/H3[0-3][0-9]/)) colorClass = 'bg-red-100 text-red-800' // Santé aigüe
              if (code.match(/H3[4-7][0-9]/)) colorClass = 'bg-purple-100 text-purple-800' // CMR/organes
              if (code.match(/H4[0-9]{2}/)) colorClass = 'bg-green-100 text-green-800' // Environnement

              return (
                <div
                  key={code}
                  className={`flex items-start gap-3 p-3 rounded-lg ${colorClass}`}
                >
                  <span className="font-mono font-bold text-sm whitespace-nowrap">
                    {code}
                  </span>
                  <span className="text-sm">{description}</span>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-gray-500 italic">Aucun code H associé</p>
        )}
      </div>
    </Card>
  )
}
